import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { CheckIcon, StarIcon } from '../components/Icons';
import { getHeroes, getServices, getProjects, getTestimonials } from '../services/api';
import './Home.css';

// Featured Software Projects - Using actual client images
const softwareHighlights = [
  {
    id: 'ccb-nyc',
    title: 'CCB NYC Website & App',
    client: 'CCB NYC Church',
    description: 'Church website and mobile app featuring service times, events, sermon archives, and community engagement tools.',
    tech: ['React', 'React Native', 'Node.js', 'PostgreSQL'],
    image: 'https://www.ccb-nyc.com/images/home-hero.jpg'
  },
  {
    id: 'svt-portal',
    title: 'SVT Portal & App',
    client: 'SVT Consulting',
    description: 'Business management system handling clients, service calls, invoicing, HR, and team communication.',
    tech: ['React', 'React Native', 'Node.js', 'AWS S3'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
  }
];

const Home = () => {
  const [hero, setHero] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroData, servicesData, projectsData, testimonialsData] = await Promise.all([
          getHeroes(),
          getServices(),
          getProjects({ featured: 'true', limit: 6 }),
          getTestimonials({ featured: 'true', limit: 3 })
        ]);
        setHero(heroData.home);
        setServices(servicesData);
        setProjects(projectsData);
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const defaultServiceImages = {
    'audio-video': 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=600&h=400&fit=crop',
    'cctv': 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600&h=400&fit=crop',
    'automation': 'https://images.unsplash.com/photo-1558002038-1055907df827?w=600&h=400&fit=crop',
    'software': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=400&fit=crop'
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} size={18} filled={i < rating} color="#FF5722" />
    ));
  };

  return (
    <div className="home-page">
      <Hero
        title={hero?.title || <>Transform Your Space with <span>Smart Technology</span></>}
        subtitle={hero?.subtitle || 'Professional Audio/Video, Security, Automation & Software solutions for homes and businesses.'}
        backgroundImage={hero?.image_url}
        ctaText="Get Free Quote"
        ctaLink="/contact"
        secondaryCtaText="Our Services"
        secondaryCtaLink="/services"
        overlay={hero?.overlay_opacity || 0.6}
      />

      {/* Services Section */}
      <section className="section services-section">
        <div className="container">
          <div className="section-title">
            <h2>Our Services</h2>
            <p>Comprehensive solutions for all your technology needs</p>
          </div>
          <div className="services-grid">
            {(services.length > 0 ? services : defaultServices).map((service, index) => {
              const imageUrl = service.image_url || defaultServiceImages[service.service_key];
              return (
                <Link 
                  to={`/services/${service.service_key}`} 
                  className="service-card" 
                  key={service.service_key || index}
                >
                  <div 
                    className="service-image"
                    style={{ 
                      backgroundImage: `url(${imageUrl})`,
                      height: '240px',
                      minHeight: '240px',
                      maxHeight: '240px'
                    }}
                  >
                    <div className="service-image-overlay"></div>
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.short_description}</p>
                    <span className="service-link">Learn More →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Software Development Showcase */}
      <section className="section section-dark software-showcase">
        <div className="container">
          <div className="section-title">
            <h2>Custom Software Development</h2>
            <p>Full-stack web applications and mobile apps built from scratch</p>
          </div>
          <div className="software-highlights-grid">
            {softwareHighlights.map(project => (
              <div className="software-highlight-card" key={project.id}>
                <div 
                  className="highlight-image"
                  style={{ backgroundImage: `url(${project.image})` }}
                />
                <div className="highlight-content">
                  <h3>{project.title}</h3>
                  <p className="highlight-client">{project.client}</p>
                  <p className="highlight-description">{project.description}</p>
                  <div className="highlight-tech">
                    {project.tech.map((t, i) => (
                      <span key={i} className="tech-pill">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/portfolio" className="btn btn-primary">View Full Portfolio</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section about-preview">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>Why Choose SVT Consulting?</h2>
              <p>
                With years of experience in audio/video installation, security systems, 
                smart home automation, and custom software development, we deliver 
                solutions that exceed expectations.
              </p>
              <ul className="about-features">
                <li>
                  <CheckIcon size={24} color="#FF5722" />
                  <span>Expert Installation & Integration</span>
                </li>
                <li>
                  <CheckIcon size={24} color="#FF5722" />
                  <span>Customized Solutions for Your Needs</span>
                </li>
                <li>
                  <CheckIcon size={24} color="#FF5722" />
                  <span>Ongoing Support & Maintenance</span>
                </li>
                <li>
                  <CheckIcon size={24} color="#FF5722" />
                  <span>Licensed & Insured Professionals</span>
                </li>
              </ul>
              <Link to="/about" className="btn btn-primary">About Us</Link>
            </div>
            <div className="about-stats">
              <div className="stat-item">
                <span className="stat-number">500+</span>
                <span className="stat-label">Projects Completed</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">10+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Support Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      {projects.length > 0 && (
        <section className="section projects-section">
          <div className="container">
            <div className="section-title">
              <h2>Featured Projects</h2>
              <p>See our latest work and installations</p>
            </div>
            <div className="projects-grid">
              {projects.map(project => (
                <Link to={`/portfolio/${project.slug}`} className="project-card" key={project.id}>
                  <div 
                    className="project-image"
                    style={{ backgroundImage: `url(${project.featured_image_url || 'https://via.placeholder.com/600x400'})` }}
                  >
                    <div className="project-overlay">
                      <span className="view-project">View Project</span>
                    </div>
                  </div>
                  <div className="project-info">
                    <h3>{project.title}</h3>
                    {project.services_provided && (
                      <div className="project-tags">
                        {project.services_provided.slice(0, 2).map((tag, i) => (
                          <span key={i} className="project-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/portfolio" className="btn btn-dark">View All Projects</Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="section section-gray testimonials-section">
          <div className="container">
            <div className="section-title">
              <h2>What Our Clients Say</h2>
              <p>Don't just take our word for it</p>
            </div>
            <div className="testimonials-grid">
              {testimonials.map(testimonial => (
                <div className="testimonial-card" key={testimonial.id}>
                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating || 5)}
                  </div>
                  <p className="testimonial-text">"{testimonial.testimonial_text}"</p>
                  <div className="testimonial-author">
                    {testimonial.client_photo_url && (
                      <img src={testimonial.client_photo_url} alt={testimonial.client_name} />
                    )}
                    <div>
                      <strong>{testimonial.client_name}</strong>
                      {testimonial.client_title && (
                        <span>{testimonial.client_title}{testimonial.client_company && `, ${testimonial.client_company}`}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Contact us today for a free consultation and quote</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
            <a href="tel:+18453043934" className="btn btn-outline">Call Now</a>
          </div>
        </div>
      </section>
    </div>
  );
};

const defaultServices = [
  { service_key: 'audio-video', title: 'Audio/Video Systems', short_description: 'Professional audio and video installation for homes and businesses.' },
  { service_key: 'cctv', title: 'CCTV & Security', short_description: 'Comprehensive security camera systems to protect your property.' },
  { service_key: 'automation', title: 'Smart Automation', short_description: 'Transform your space with intelligent automation.' },
  { service_key: 'software', title: 'Software Development', short_description: 'Custom software solutions including websites and mobile apps.' }
];

export default Home;
